"use client"

import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import {
  ArrowLeft,
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FolderTree,
  Layers,
  Loader2,
  PackageCheck,
  Ruler,
  Scale,
  Sparkles,
  Tag,
  Truck,
  Upload,
} from "lucide-react"
import toast from "react-hot-toast"
import {
  confirmInventoryImport,
  exportInventoryData,
  resolveInventoryImportMasters,
  validateInventoryImport,
} from "../../../services/inventoryImportExportService"

const getResolutionKey = (r) => `${r.field}:${(r.category || "").toLowerCase()}:${r.value.toLowerCase()}`

const FIELD_LABELS = {
  category: "Category",
  subCategory: "Sub Category",
  unit: "Unit",
  size: "Size",
  brand: "Brand",
  vendor: "Vendor",
}

const FIELD_ICONS = {
  category: FolderTree,
  subCategory: Layers,
  unit: Scale,
  size: Ruler,
  brand: Tag,
  vendor: Truck,
}

const STEPS = [
  { key: "upload", label: "Upload" },
  { key: "resolve", label: "Resolve values" },
  { key: "preview", label: "Review" },
  { key: "done", label: "Done" },
]

const PREVIEW_PAGE_SIZE = 10
const RESOLUTION_ROWS_PREVIEW_LIMIT = 20

const allowedTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
]

const Stepper = ({ step }) => {
  const currentIndex = STEPS.findIndex((s) => s.key === step)
  return (
    <ol className="flex items-center w-full mb-1">
      {STEPS.map((s, i) => (
        <li key={s.key} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center gap-1.5">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors",
                i < currentIndex && "bg-green-600 border-green-600 text-white",
                i === currentIndex && "bg-[#0263cc] border-[#0263cc] text-white",
                i > currentIndex && "bg-white border-slate-300 text-slate-400",
              )}
            >
              {i < currentIndex ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={cn(
                "text-[11px] font-medium whitespace-nowrap",
                i <= currentIndex ? "text-slate-700" : "text-slate-400",
              )}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={cn("flex-1 h-0.5 mx-2 rounded-full", i < currentIndex ? "bg-green-500" : "bg-slate-200")} />
          )}
        </li>
      ))}
    </ol>
  )
}

const ResolutionCard = ({ resolution: r, decision, onUpdate, itemNameByRow }) => {
  const Icon = FIELD_ICONS[r.field] || Tag
  const action = decision.action
  const rows = r.rows || []

  const choose = (nextAction, finalValue) => onUpdate({ action: nextAction, finalValue })

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {FIELD_LABELS[r.field] || r.field}
              {r.category && <span className="normal-case text-slate-400"> · under {r.category}</span>}
            </p>
            <p className="font-semibold text-slate-900">Uploaded as "{r.value}"</p>
          </div>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <button type="button" className="flex-shrink-0">
              <Badge
                variant="outline"
                className="cursor-pointer hover:border-[#0263cc] hover:text-[#0263cc] transition-colors"
              >
                {r.count} row{r.count > 1 ? "s" : ""}
              </Badge>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Affected rows — {FIELD_LABELS[r.field] || r.field} "{r.value}"
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-80 overflow-y-auto -mx-1 px-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                    <th className="pb-2 font-medium">Row</th>
                    <th className="pb-2 font-medium">Item Name</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, RESOLUTION_ROWS_PREVIEW_LIMIT).map((rowNumber) => (
                    <tr key={rowNumber} className="border-b border-slate-50">
                      <td className="py-2 text-slate-500">{rowNumber}</td>
                      <td className="py-2 font-medium text-slate-900">
                        {itemNameByRow?.[rowNumber] || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > RESOLUTION_ROWS_PREVIEW_LIMIT && (
                <p className="text-xs text-slate-400 mt-2 text-center">
                  + {rows.length - RESOLUTION_ROWS_PREVIEW_LIMIT} more row
                  {rows.length - RESOLUTION_ROWS_PREVIEW_LIMIT > 1 ? "s" : ""} not shown
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {r.type === "similar" && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-sm text-amber-700">
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          Did you mean <strong>"{r.suggestion}"</strong>?
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {r.type === "similar" && (
          <button
            type="button"
            onClick={() => choose("use", r.suggestion)}
            className={cn(
              "text-sm font-medium px-3.5 py-2 rounded-xl border transition-colors",
              action === "use"
                ? "bg-[#0263cc] border-[#0263cc] text-white"
                : "border-slate-200 text-slate-700 hover:border-[#0263cc]",
            )}
          >
            Use "{r.suggestion}"
          </button>
        )}
        <button
          type="button"
          onClick={() => choose("create", r.value)}
          className={cn(
            "text-sm font-medium px-3.5 py-2 rounded-xl border transition-colors",
            action === "create"
              ? "bg-[#0263cc] border-[#0263cc] text-white"
              : "border-slate-200 text-slate-700 hover:border-[#0263cc]",
          )}
        >
          Create new master
        </button>
        <button
          type="button"
          onClick={() => choose("edit", decision.finalValue ?? r.value)}
          className={cn(
            "text-sm font-medium px-3.5 py-2 rounded-xl border transition-colors",
            action === "edit"
              ? "bg-[#0263cc] border-[#0263cc] text-white"
              : "border-slate-200 text-slate-700 hover:border-[#0263cc]",
          )}
        >
          Edit value
        </button>
      </div>

      {action === "edit" ? (
        <Input
          className="w-full sm:w-64"
          value={decision.finalValue}
          onChange={(e) => onUpdate({ action: "edit", finalValue: e.target.value })}
          placeholder="Type the correct value"
        />
      ) : action ? (
        <p className="text-xs text-slate-500">
          Final value: <span className="font-medium text-slate-700">{decision.finalValue}</span>
        </p>
      ) : (
        <p className="text-xs text-slate-400">Choose an action for this value.</p>
      )}
    </div>
  )
}

const InventoryImportExport = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [validating, setValidating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [exportingData, setExportingData] = useState(false)
  const [validationResult, setValidationResult] = useState(null)
  const [errors, setErrors] = useState([])
  const [step, setStep] = useState("upload")
  const [rowFilter, setRowFilter] = useState("all")
  const [previewPage, setPreviewPage] = useState(1)
  const [resolutions, setResolutions] = useState([])
  const [resolutionDecisions, setResolutionDecisions] = useState({})
  const [resolving, setResolving] = useState(false)
  const [importResult, setImportResult] = useState(null)

  const triggerDownload = (blobData, fileName) => {
    const blob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleExport = async () => {
    try {
      setExportingData(true)
      const data = await exportInventoryData()
      triggerDownload(data, "inventory-export.xlsx")
      toast.success("Inventory exported")
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to export inventory")
    } finally {
      setExportingData(false)
    }
  }

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return
    const extensionAllowed = selectedFile.name.toLowerCase().endsWith(".xlsx") || selectedFile.name.toLowerCase().endsWith(".xls")
    if (!allowedTypes.includes(selectedFile.type) && !extensionAllowed) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)")
      return
    }
    setFile(selectedFile)
    setValidationResult(null)
    setErrors([])
    setStep("upload")
    setRowFilter("all")
    setPreviewPage(1)
    setResolutions([])
    setResolutionDecisions({})
    setImportResult(null)
  }

  const initDecisionsFromResolutions = (groups) => {
    const decisions = {}
    groups.forEach((r) => {
      const key = getResolutionKey(r)
      decisions[key] = r.type === "similar" ? {} : { action: "create", finalValue: r.value }
    })
    return decisions
  }

  const applyValidationData = (data) => {
    setValidationResult(data)
    setErrors(data.errors || [])
    setRowFilter("all")
    setPreviewPage(1)
    const groups = data.resolutions || []
    setResolutions(groups)
    if (groups.length) {
      setResolutionDecisions(initDecisionsFromResolutions(groups))
      setStep("resolve")
    } else {
      setStep("preview")
    }
  }

  const handleValidate = async () => {
    if (!file) {
      toast.error("Select a file first")
      return
    }
    try {
      setValidating(true)
      const data = await validateInventoryImport(file)
      applyValidationData(data)
      if (data.resolutions?.length) {
        toast.success(`${data.resolutions.length} item(s) need a quick decision before import`)
      } else {
        toast.success(`Validated ${data.validRecords?.length || 0} records`)
      }
    } catch (error) {
      const message = error.response?.data?.message || "Validation failed"
      const backendErrors = error.response?.data?.errors || []
      setErrors(backendErrors)
      toast.error(message)
    } finally {
      setValidating(false)
    }
  }

  const updateDecision = (key, patch) => {
    setResolutionDecisions((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  }

  const handleResolveMasters = async () => {
    if (!file) return
    const payload = resolutions.map((r) => {
      const decision = resolutionDecisions[getResolutionKey(r)] || {}
      return {
        field: r.field,
        value: r.value,
        category: r.category || undefined,
        finalValue: decision.finalValue || r.value,
      }
    })
    try {
      setResolving(true)
      const data = await resolveInventoryImportMasters(file, payload)
      applyValidationData(data)
      if (data.resolutions?.length) {
        toast.error(`${data.resolutions.length} item(s) still need a decision`)
      } else {
        toast.success(`Masters created — ${data.validRecords?.length || 0} records ready to import`)
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resolve masters")
    } finally {
      setResolving(false)
    }
  }

  const handleConfirmImport = async () => {
    const records = validationResult?.validRecords || []
    if (!records.length) {
      toast.error("No valid records to import")
      return
    }
    try {
      setImporting(true)
      const data = await confirmInventoryImport(records)
      setImportResult(data)
      setStep("done")
      toast.success(`Import completed: ${data.summary?.successful || 0} successful`)
    } catch (error) {
      toast.error(error.response?.data?.message || "Import failed")
    } finally {
      setImporting(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setValidationResult(null)
    setErrors([])
    setStep("upload")
    setRowFilter("all")
    setPreviewPage(1)
    setResolutions([])
    setResolutionDecisions({})
    setImportResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const previewRows = useMemo(() => validationResult?.preview || [], [validationResult])
  const itemNameByRow = useMemo(
    () => Object.fromEntries(previewRows.map((row) => [row.row, row.item_name])),
    [previewRows],
  )
  const validCount = validationResult?.validRecords?.length || 0
  const warningRows = useMemo(() => previewRows.filter((row) => row.warnings?.length > 0), [previewRows])

  const filteredPreviewRows = useMemo(() => {
    if (rowFilter === "all") return previewRows
    if (rowFilter === "warning") return warningRows
    return previewRows.filter((row) => row.status === rowFilter)
  }, [previewRows, warningRows, rowFilter])

  const totalPreviewPages = Math.max(1, Math.ceil(filteredPreviewRows.length / PREVIEW_PAGE_SIZE))
  const currentPreviewPage = Math.min(previewPage, totalPreviewPages)
  const paginatedPreviewRows = filteredPreviewRows.slice(
    (currentPreviewPage - 1) * PREVIEW_PAGE_SIZE,
    currentPreviewPage * PREVIEW_PAGE_SIZE,
  )

  const setFilterAndResetPage = (nextFilter) => {
    setRowFilter(nextFilter)
    setPreviewPage(1)
  }

  return (
    <div className="min-h-[calc(100vh-80px)] w-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 py-4 md:py-6">
      <div className="mx-auto w-full space-y-6">
        <div className="flex items-center justify-between gap-4 sticky top-0 z-10 bg-gradient-to-r from-slate-50 via-slate-50/95 to-slate-50/90 pb-3 md:pb-4 backdrop-blur-md border-b border-slate-200/60">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full border-slate-200 bg-white hover:bg-slate-50 shadow-sm"
              onClick={() => navigate("/dashboard/inventory")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">Inventory Import / Export</h1>
              <p className="text-xs sm:text-sm text-slate-500">Download template, export current inventory, validate and import updates</p>
            </div>
          </div>
        </div>

        <Card className="border border-slate-200/80 shadow-sm rounded-2xl bg-white/95">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#0263cc]" />
              Data Operations
            </CardTitle>
            <CardDescription>Use export for existing data, edit in Excel, then import again</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Button
                className="bg-[#0263cc] hover:bg-[#0155af]"
                onClick={handleExport}
                disabled={exportingData}
              >
                {exportingData ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Export Inventory
              </Button>
              <p className="text-xs text-slate-500">
                Note: Use the exported file as your template, update values, then import the same file.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200/80 shadow-sm rounded-2xl bg-white/95">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#0263cc]" />
              Import Inventory File
            </CardTitle>
            <CardDescription>Upload exported/template file, validate, preview, and confirm import</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Stepper step={step} />

            {validationResult?.sheetNote && step !== "done" && (
              <Alert className="border-amber-200 bg-amber-50/60">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-slate-700">{validationResult.sheetNote}</AlertDescription>
              </Alert>
            )}

            {step === "upload" && (
              <>
                <div
                  className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#0263cc] transition"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Upload className="h-7 w-7 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-700">{file ? file.name : "Click to select Excel file"}</p>
                  <p className="text-xs text-slate-500 mt-1">Supported: .xlsx, .xls</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleReset} disabled={!file}>
                    Clear
                  </Button>
                  <Button onClick={handleValidate} disabled={!file || validating} className="bg-[#0263cc] hover:bg-[#0155af]">
                    {validating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Validate & Preview
                  </Button>
                </div>
              </>
            )}

            {step === "resolve" && (
              <div className="space-y-4">
                <Alert className="border-blue-200 bg-blue-50/60">
                  <Sparkles className="h-4 w-4 text-[#0263cc]" />
                  <AlertDescription className="text-slate-700">
                    {resolutions.length} value(s) in your file don't match existing masters yet. Pick an action for
                    each — your decision applies to every affected row automatically, no need to fix the file and
                    re-upload.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  {resolutions.map((r) => {
                    const key = getResolutionKey(r)
                    return (
                      <ResolutionCard
                        key={key}
                        resolution={r}
                        decision={resolutionDecisions[key] || {}}
                        onUpdate={(patch) => updateDecision(key, patch)}
                        itemNameByRow={itemNameByRow}
                      />
                    )
                  })}
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleReset} disabled={resolving}>
                    Start Over
                  </Button>
                  <Button
                    onClick={handleResolveMasters}
                    disabled={resolving || resolutions.some((r) => !resolutionDecisions[getResolutionKey(r)]?.finalValue)}
                    className="bg-[#0263cc] hover:bg-[#0155af]"
                  >
                    {resolving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create Masters & Continue Import
                  </Button>
                </div>
              </div>
            )}

            {step === "preview" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterAndResetPage(rowFilter === "valid" ? "all" : "valid")}
                    className={cn(
                      "cursor-pointer transition ring-1",
                      rowFilter === "valid" ? "ring-green-500 bg-green-50/60" : "ring-transparent hover:ring-green-200",
                    )}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{validCount}</div>
                      <div className="text-xs text-slate-500 mt-1">Valid Records</div>
                      <div className="text-[10px] text-slate-400 mt-1">{rowFilter === "valid" ? "Showing only these" : "Click to filter"}</div>
                    </CardContent>
                  </Card>
                  <Card
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterAndResetPage(rowFilter === "invalid" ? "all" : "invalid")}
                    className={cn(
                      "cursor-pointer transition ring-1",
                      rowFilter === "invalid" ? "ring-red-500 bg-red-50/60" : "ring-transparent hover:ring-red-200",
                    )}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-red-600">{errors.length}</div>
                      <div className="text-xs text-slate-500 mt-1">Errors</div>
                      <div className="text-[10px] text-slate-400 mt-1">{rowFilter === "invalid" ? "Showing only these" : "Click to filter"}</div>
                    </CardContent>
                  </Card>
                  <Card
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterAndResetPage(rowFilter === "warning" ? "all" : "warning")}
                    className={cn(
                      "cursor-pointer transition ring-1",
                      rowFilter === "warning" ? "ring-amber-500 bg-amber-50/60" : "ring-transparent hover:ring-amber-200",
                    )}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-amber-600">{warningRows.length}</div>
                      <div className="text-xs text-slate-500 mt-1">Warnings</div>
                      <div className="text-[10px] text-slate-400 mt-1">{rowFilter === "warning" ? "Showing only these" : "Click to filter"}</div>
                    </CardContent>
                  </Card>
                  <Card
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterAndResetPage("all")}
                    className={cn(
                      "cursor-pointer transition ring-1",
                      rowFilter === "all" ? "ring-[#0263cc] bg-blue-50/60" : "ring-transparent hover:ring-blue-200",
                    )}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-[#0263cc]">{validationResult?.totalRecords || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">Total Rows</div>
                      <div className="text-[10px] text-slate-400 mt-1">{rowFilter === "all" ? "Showing all" : "Click to show all"}</div>
                    </CardContent>
                  </Card>
                </div>

                {errors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {errors.map((err, index) => (
                          <p key={`${err.row}-${index}`} className="text-sm">
                            <strong>Row {err.row}:</strong> {err.error}
                          </p>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Sub Category</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPreviewRows.map((row, idx) => (
                        <TableRow key={`${row.row}-${idx}`}>
                          <TableCell>{row.inventory_id || "-"}</TableCell>
                          <TableCell>{row.item_name || "-"}</TableCell>
                          <TableCell>{row.category || "-"}</TableCell>
                          <TableCell>{row.sub_category || "-"}</TableCell>
                          <TableCell>{row.vendor_name || "-"}</TableCell>
                          <TableCell>{row.brand || "-"}</TableCell>
                          <TableCell>{row.quantity_available ?? 0}</TableCell>
                          <TableCell>
                            <Badge
                              variant={row.status === "invalid" ? "destructive" : "outline"}
                              className={
                                row.status === "valid"
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : row.status === "pending_resolution"
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : ""
                              }
                            >
                              {row.status === "valid" && <CheckCircle className="h-3 w-3 mr-1" />}
                              {row.status === "pending_resolution" && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {row.status === "invalid" && <AlertCircle className="h-3 w-3 mr-1" />}
                              {row.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[220px] text-xs text-slate-600">
                            {row.reason || (row.status === "valid" ? "All fields matched" : "-")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {filteredPreviewRows.length === 0 ? (
                  <p className="text-xs text-slate-500">No rows match this filter.</p>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Showing {(currentPreviewPage - 1) * PREVIEW_PAGE_SIZE + 1}-
                      {Math.min(currentPreviewPage * PREVIEW_PAGE_SIZE, filteredPreviewRows.length)} of {filteredPreviewRows.length} rows
                    </p>
                    {totalPreviewPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPreviewPage <= 1}
                          onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-slate-500">
                          Page {currentPreviewPage} of {totalPreviewPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPreviewPage >= totalPreviewPages}
                          onClick={() => setPreviewPage((p) => Math.min(totalPreviewPages, p + 1))}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleReset} disabled={importing}>
                    Start Over
                  </Button>
                  <Button onClick={handleConfirmImport} disabled={importing || !validCount} className="bg-[#0263cc] hover:bg-[#0155af]">
                    {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Confirm Import
                  </Button>
                </div>
              </div>
            )}

            {step === "done" && importResult && (
              <div className="space-y-4 text-center py-6">
                <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <PackageCheck className="h-7 w-7 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Import completed</h3>
                  <p className="text-sm text-slate-500 mt-1">Your inventory has been updated.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto text-left">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xl font-bold text-green-600">{importResult.summary?.successful || 0}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Imported</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xl font-bold text-red-600">{importResult.summary?.failed || 0}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Failed</p>
                  </div>
                </div>
                <div className="flex justify-center gap-2 pt-2">
                  <Button variant="outline" onClick={handleReset}>
                    Import Another File
                  </Button>
                  <Button className="bg-[#0263cc] hover:bg-[#0155af]" onClick={() => navigate("/dashboard/inventory")}>
                    Go to Inventory
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default InventoryImportExport
